import React, { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import SpinnerNew from '../../components/main/SpinnerNew/SpinnerNew';
import clsx from 'clsx';
import { DialogStackProvider, useDialogDepth } from './DialogStackContext';

type DialogBaseProps = {
    title: ReactNode,
    visible: boolean,
    type: "OkCancel" | "YesNo" | "Custom";
    onClose: () => void,
    onOkClick?: () => void,
    onYesClick?: () => void,
    onSubmit?: () => void,
    validationError?: string,
    confirmLabel?: ReactNode,
    width?: string,
    isLoading?: boolean,
    actions?: ReactNode | undefined,
    headerActions?: ReactNode | undefined,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const DialogBase = (props: DialogBaseProps) => {
    const { style, actions, headerActions, className, children, title, visible, type, isLoading, width, onClose, onOkClick, onYesClick, onSubmit, validationError, confirmLabel } = props;
    const overlayRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const depth = useDialogDepth();

    // Only the first dialog (depth 0) shows the dark backdrop.
    // Nested dialogs get a transparent overlay (still catches clicks).
    const isNested = depth > 0;
    // Subtle cascade: small downward shift per level, minimal horizontal
    const offsetX = isNested ? Math.min(depth * 8, 24) : 0;
    const offsetY = isNested ? Math.min(depth * 12, 36) : 0;

    // Close on Escape key
    useEffect(() => {
        if (!visible) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [visible, onClose]);

    // Lock body scroll when dialog is open
    useEffect(() => {
        if (!visible) return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [visible]);

    // Close on backdrop click
    const handleOverlayClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.target === overlayRef.current) {
            onClose();
        }
    };

    if (!visible) return null;

    const isDestructive = type === 'YesNo';

    return createPortal(
        <DialogStackProvider>
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className="fixed inset-0 flex items-center justify-center p-4 animate-fade-in"
            style={{
                zIndex: 50 + depth,
                backgroundColor: isNested ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.6)',
                backdropFilter: isNested ? 'none' : 'blur(6px)',
            }}
        >
            <div
                ref={panelRef}
                className={clsx(
                    'relative rounded-2xl overflow-hidden animate-scale-in',
                    className
                )}
                style={{
                    width: width || '480px',
                    maxWidth: '90vw',
                    maxHeight: '85vh',
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-default)',
                    boxShadow: isNested
                        ? '0 25px 60px -10px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0,0,0,0.06)'
                        : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.03)',
                    transform: (offsetX || offsetY) ? `translate(${offsetX}px, ${offsetY}px)` : undefined,
                    ...style,
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-4"
                    style={{
                        borderBottom: '1px solid var(--border-default)',
                        backgroundColor: 'var(--surface-50)',
                    }}
                >
                    <h2 className="text-base font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>
                        {title}
                    </h2>
                    <div className="flex items-center gap-1">
                        {headerActions}
                        <button
                            onClick={onClose}
                        className="p-1.5 rounded-lg transition-colors duration-150"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-tertiary)',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-tertiary)';
                        }}
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div
                    className="dialog-body px-6 py-5 overflow-y-auto"
                    style={{
                        maxHeight: 'calc(85vh - 140px)',
                        color: 'var(--text-secondary)',
                    }}
                >
                    {validationError && (
                        <div
                            className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg text-sm"
                            style={{
                                backgroundColor: 'var(--danger-soft)',
                                color: 'var(--danger)',
                                border: '1px solid var(--danger)',
                                margin: '12px 16px',
                            }}
                        >
                            {validationError}
                        </div>
                    )}
                    {children}
                </div>

                {/* Actions */}
                {(actions || type !== "Custom") && (
                    <div
                        className="flex items-center justify-end gap-3 px-6 py-4"
                        style={{
                            borderTop: '1px solid var(--border-default)',
                            backgroundColor: 'var(--surface-50)',
                        }}
                    >
                        {type === "OkCancel" && (
                            <>
                                <button className="btn btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={() => { onSubmit ? onSubmit() : onOkClick?.(); }}>
                                    {confirmLabel ?? 'Confirm'}
                                </button>
                            </>
                        )}
                        {type === "YesNo" && (
                            <>
                                <button className="btn btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button className="btn btn-danger" onClick={() => onYesClick?.()}>
                                    {confirmLabel ?? 'Delete'}
                                </button>
                            </>
                        )}
                        {type === "Custom" && actions}
                    </div>
                )}

                {/* Loading overlay */}
                {isLoading && (
                    <div
                        className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl"
                        style={{ backgroundColor: 'var(--surface-0)', opacity: 0.8 }}
                    >
                        <SpinnerNew size="large" />
                    </div>
                )}
            </div>
        </div>
        </DialogStackProvider>,
        document.body
    );
};

export default DialogBase;
